import { useState } from 'react';
import { Campo } from '../../../componentes/Campo';
import { Interruptor } from '../../../componentes/Interruptor';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Contador } from '../../../componentes/Contador';
import { Bloque, Caja, FechaHora } from '../comun';
import { deLocal, useAvisarError, useCarga } from '../uso';
import { cuandoEs, cupoEscrito, opsDe } from '../textos';
import type { ContextoListas } from './ApartadoListas';

/**
 * Crear las listas de un evento (10.9, `createGuestLists`, H-27): desde las plantillas activas que aún no tiene (se copian
 * con sus ajustes; la hora límite se cuelga del día del evento) y una lista suelta solo para este evento.
 */
export function CrearListas({ ctx }: { ctx: ContextoListas }) {
    const { props, evento, listas } = ctx;
    const { negocio, movil } = props;
    const ops = opsDe(props.ops);
    const avisar = useAvisoBreve();
    const avisarError = useAvisarError();
    const plantillas = useCarga(() => ops.listGuestListTemplates(negocio, { status: 'active', limit: 100 }), [negocio]);
    const usadas = new Set(listas.filter((l) => l.status !== 'cancelled').map((l) => l.template_id));
    const libres = (plantillas.datos?.data ?? []).filter((t) => t.status === 'active' && !usadas.has(t.id));
    const [elegidas, setElegidas] = useState<Readonly<Record<string, boolean>>>({});
    const [suelta, setSuelta] = useState(false);
    const [nombre, setNombre] = useState('');
    const [aforo, setAforo] = useState('');
    const [acomp, setAcomp] = useState(1);
    const [limite, setLimite] = useState('');
    const [probado, setProbado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const marcada = (id: string, porDefecto: boolean) => elegidas[id] ?? porDefecto;
    const ids = libres.filter((t) => marcada(t.id, t.apply_by_default ?? false)).map((t) => t.id);
    const n = ids.length + (suelta ? 1 : 0);
    const errNombre = probado && suelta && nombre.trim().length < 2 ? 'Escribe el nombre de la lista.' : null;
    const errAforo = probado && suelta && cupoEscrito(aforo) === undefined ? 'Un número entero o vacío.' : null;

    const crear = () => {
        if (evento === null) {
            return;
        }
        if (n === 0 || (suelta && (nombre.trim().length < 2 || cupoEscrito(aforo) === undefined))) {
            setProbado(true);
            return;
        }
        setOcupado(true);
        ops.createGuestLists(negocio, evento.id, {
            template_ids: ids,
            custom_lists: suelta ? [{ name: nombre.trim(), capacity: cupoEscrito(aforo) ?? null, max_companions: acomp, door_cutoff_at: deLocal(limite) }] : [],
        })
            .then((r) => {
                avisar({ tipo: 'ok', texto: `${r.data.length === 1 ? 'Lista creada' : `${String(r.data.length)} listas creadas`} en ${evento.name ?? ''}. Ya puedes asignar a tus RRPP.` });
                ctx.recargar();
                ctx.abrir(null);
            })
            .catch((e: unknown) => { avisarError(e); })
            .finally(() => { setOcupado(false); });
    };

    return (
        <PanelLateral
            abierto
            onCerrar={() => { ctx.abrir(null); }}
            movil={movil}
            titulo="Crear las listas"
            eyebrow={evento?.name ?? ''}
            subtitulo={cuandoEs(evento?.starts_at)}
            icono="list-checks"
            accion={n > 1 ? `Crear ${String(n)} listas` : 'Crear la lista'}
            accionIcono="plus"
            inactiva={n === 0}
            ocupado={ocupado}
            ocupadoTexto="Creando…"
            onAccion={crear}
        >
            <div className="rv-ca-cuerpo">
                <Bloque titulo="Desde tus plantillas" intro="Se copian con sus ajustes; la hora límite se cuelga del día del evento.">
                    {plantillas.datos === null ? (
                        <p className="rv-ca-nota rv-ca-nota--gris">Cargando…</p>
                    ) : libres.length === 0 ? (
                        <Caja icono="check">Este evento ya tiene una lista de cada plantilla.</Caja>
                    ) : (
                        libres.map((t) => (
                            <Interruptor
                                key={t.id}
                                etiqueta={t.name ?? ''}
                                valor={marcada(t.id, t.apply_by_default ?? false)}
                                onChange={(v) => { setElegidas({ ...elegidas, [t.id]: v }); }}
                                ayuda={`${t.capacity == null ? 'Sin límite' : `${String(t.capacity)} personas`}${t.door_cutoff_local_time == null ? '' : ` · hasta las ${t.door_cutoff_local_time}`} · ${t.public_signup === true ? 'alta pública' : 'solo el equipo'}`}
                            />
                        ))
                    )}
                </Bloque>
                <Bloque titulo="Una lista solo para este evento">
                    <Interruptor etiqueta="Añadir una lista suelta" valor={suelta} onChange={setSuelta} ayuda="Por ejemplo, «Prensa» o «Artistas»." />
                    {suelta && (
                        <>
                            <Campo etiqueta="Nombre de la lista" valor={nombre} onChange={setNombre} error={errNombre} max={70} nombre="nombre-lista" />
                            <Campo etiqueta="Aforo propio" valor={aforo} onChange={setAforo} modo="numeric" error={errAforo} ayuda="Vacío = sin límite propio." />
                            <Contador min={0} etiqueta="Acompañantes" valor={acomp} max={10} ayuda="Cuántos puede traer cada invitado." onCambio={setAcomp} />
                            <FechaHora etiqueta="Hora límite de entrada" valor={limite} onChange={setLimite} ayuda="Fuera de hora, la puerta lo marca en ámbar. Vacía = sin hora límite." />
                        </>
                    )}
                </Bloque>
            </div>
        </PanelLateral>
    );
}
